export const OPERATIONS_METRICS = [
  { key: 'heat', label: '热度', unit: '热度值', decimal: true },
  { key: 'favorites', label: '收藏量', unit: '次' },
  { key: 'episodeOneLikes', label: '第一集点赞数', unit: '次' },
  { key: 'episodeOneComments', label: '第一集评论数', unit: '条' },
];

const MAX_SAFE_COUNT = BigInt(Number.MAX_SAFE_INTEGER);
const EMPTY_DRAFT = { heat: '', favorites: '', episodeOneLikes: '', episodeOneComments: '', notes: '' };

export function shanghaiToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  return ['year', 'month', 'day'].map(key => parts.find(part => part.type === key).value).join('-');
}

/** Expand display units with decimal arithmetic, never Number multiplication. */
export function parseOperationsMetric(input, { decimal = false, label = '数值' } = {}) {
  const text = String(input ?? '').trim();
  if (!text) return null;
  if (text.length > 80) throw new Error(`${label}数值过长`);
  const match = /^(\d+)(?:\.(\d+))?\s*(万|亿)?$/.exec(text);
  if (!match) throw new Error(`${label}请填写非负数字，可带“万”或“亿”，不支持负数、逗号或科学计数法`);
  const [, integer, fraction = '', unit] = match;
  const scale = fraction.length - (unit === '万' ? 4 : unit === '亿' ? 8 : 0);
  let digits = BigInt(integer + fraction).toString();
  let expanded;
  if (scale <= 0) expanded = digits + '0'.repeat(-scale);
  else {
    digits = digits.padStart(scale + 1, '0');
    expanded = `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  }
  expanded = expanded.replace(/^0+(?=\d)/, '').replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  const [whole, tail = ''] = expanded.split('.');
  if (!decimal && tail) throw new Error(`${label}换算后必须是整数`);
  if (decimal && tail.length > 4) throw new Error(`${label}换算后最多保留 4 位小数`);
  if (BigInt(whole) > MAX_SAFE_COUNT || (BigInt(whole) === MAX_SAFE_COUNT && /[1-9]/.test(tail))) {
    throw new Error(`${label}不能超过 ${Number.MAX_SAFE_INTEGER}`);
  }
  return decimal ? expanded : Number(expanded);
}

export function metricDraft(record) {
  return { ...EMPTY_DRAFT, ...Object.fromEntries(OPERATIONS_METRICS.map(({ key }) => [key, record?.[key] == null ? '' : String(record[key])])), notes: record?.notes || '' };
}

export function metricPayload(draft, version) {
  const values = Object.fromEntries(OPERATIONS_METRICS.map(metric => [metric.key, parseOperationsMetric(draft[metric.key], metric)]));
  if (Object.values(values).every(value => value === null)) throw new Error('至少填写一项指标；未知数据留空，真实为零时填写 0');
  if (String(draft.notes || '').length > 2000) throw new Error('备注最多 2000 字');
  return { ...values, notes: String(draft.notes || '').trim(), version: version ?? null };
}

export function formatOperationsMetric(value) {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  if (!/^\d+(?:\.\d+)?$/.test(text)) return text;
  const [whole, fraction] = text.split('.');
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (fraction ? `.${fraction}` : '');
}

export function isValidMetricDate(date, publishedDate, today = shanghaiToday()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return false;
  const parsed = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date
    && date <= today && Boolean(publishedDate) && date >= publishedDate;
}

export function safeSeriesUrl(value) {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch { return null; }
}
